import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useGrowthSprintPhases } from "@/hooks/useGrowthSprintPhases";

interface PhaseSelectorProps {
  value?: string;
  onValueChange: (phaseId: string) => void;
  brandId?: string;
  methodology?: string;
  placeholder?: string;
  allowNone?: boolean;
  disabled?: boolean;
}

const getMonthBadgeColor = (month: number) => {
  switch (month) {
    case 1:
      return "bg-blue-100 text-blue-800 border-blue-200";
    case 2:
      return "bg-orange-100 text-orange-800 border-orange-200";
    case 3:
      return "bg-green-100 text-green-800 border-green-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

export const PhaseSelector: React.FC<PhaseSelectorProps> = ({ 
  value, 
  onValueChange, 
  brandId,
  methodology = '2y3x',
  placeholder = "Select a phase",
  allowNone = true,
  disabled = false
}) => {
  const { phases, isLoading } = useGrowthSprintPhases(brandId, methodology);

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled || isLoading}>
      <SelectTrigger>
        <SelectValue placeholder={isLoading ? "Loading phases..." : placeholder}>
          {value && value !== "none" ? (
            (() => {
              const selectedPhase = phases.find(p => p.id === value);
              if (!selectedPhase) return placeholder;
              return (
                <div className="flex items-center gap-2">
                  <span>{selectedPhase.name}</span>
                  <Badge className={`${getMonthBadgeColor(selectedPhase.month_of_quarter)} text-xs`}>
                    Month {selectedPhase.month_of_quarter}
                  </Badge>
                </div>
              );
            })()
          ) : (
            placeholder
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {allowNone && <SelectItem value="none">No Phase</SelectItem>}
        {phases.map((phase) => (
          <SelectItem key={phase.id} value={phase.id}>
            <div className="flex items-center gap-2">
              <span>{phase.name}</span>
              <Badge className={`${getMonthBadgeColor(phase.month_of_quarter)} text-xs`}>
                Month {phase.month_of_quarter}
              </Badge>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
